import { FC, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import classNames from 'classnames'
import SearchIcon from '@rsuite/icons/Search'
import ReloadIcon from '@rsuite/icons/Reload'
import FileUploadIcon from '@rsuite/icons/FileUpload'
import EmailIcon from '@rsuite/icons/Email'
import PageIcon from '@rsuite/icons/Page'
import { Button } from 'shared'
import useAuth from 'hooks/Auth'
import { notifyError } from 'utils'
import { ROUTES } from 'routers/Routes'
import {
  useGetClientInboxesQuery,
  useGetClientNameQuery,
  useGetDocumentFileMutation,
  useGetDocumentStatsQuery,
  useGetDocumentsQuery
} from './documentsApiSlice'
import { DocumentQueue, DocumentSummary } from './types'
import {
  QUEUE_EMPTY,
  QUEUE_TABS,
  displayFileName,
  isBusy,
  isQueue
} from './utils'
import { documentPath, downloadBlob } from './documentNav'
import DocumentsTable from './components/DocumentsTable'
import Pagination from './components/Pagination'
import PageHeader from './components/PageHeader'
import EmptyState from './components/EmptyState'
import CopyChip from './components/CopyChip'
import UploadDocumentsModal from './components/UploadDocumentsModal'
import './documents.scss'

const LIVE_POLL_MS = 8000
const SEARCH_DEBOUNCE_MS = 350
const PAGE_SIZES = [20, 50, 100]

/** Where reviewers start: the work that needs a person first. */
const DEFAULT_QUEUE: DocumentQueue = 'NEEDS_REVIEW'

const DocumentList: FC = () => {
  const navigate = useNavigate()
  const { selectedClientId } = useAuth()
  const clientId = selectedClientId ?? ''
  const [params, setParams] = useSearchParams()
  const [uploadOpen, setUploadOpen] = useState(false)

  // URL is the source of truth, so links and Back keep the view.
  const queueParam = params.get('queue')
  let queue: DocumentQueue | undefined = DEFAULT_QUEUE
  if (queueParam === 'ALL') queue = undefined
  else if (isQueue(queueParam)) queue = queueParam
  const search = params.get('q') ?? ''
  const page = Math.max(0, Number(params.get('page') ?? 0) || 0)
  const sizeParam = Number(params.get('size'))
  const size = PAGE_SIZES.includes(sizeParam) ? sizeParam : PAGE_SIZES[0]

  const [searchText, setSearchText] = useState(search)
  useEffect(() => setSearchText(search), [search])

  // Functional update: a debounced search must not undo a tab or page change
  // made in the meantime.
  const setView = (patch: Record<string, string | undefined>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        Object.entries(patch).forEach(([k, v]) =>
          v == null || v === '' ? next.delete(k) : next.set(k, v)
        )
        return next
      },
      { replace: true }
    )

  useEffect(() => {
    if (searchText === search) return undefined
    const t = setTimeout(
      () => setView({ q: searchText || undefined, page: undefined }),
      SEARCH_DEBOUNCE_MS
    )
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText])

  const listArgs = {
    clientId,
    queue,
    search: search || undefined,
    page,
    size,
    sort: 'receivedAt' as const,
    direction: 'desc' as const
  }
  const list = useGetDocumentsQuery(listArgs, { skip: !clientId })
  const stats = useGetDocumentStatsQuery({ clientId }, { skip: !clientId })
  const clientName = useGetClientNameQuery(
    { clientId },
    { skip: !clientId }
  ).data
  const inboxes = useGetClientInboxesQuery(
    { clientId },
    { skip: !clientId }
  ).data
  const inbox = inboxes?.[0]

  const rows = useMemo(() => list.data?.content ?? [], [list.data])

  // Documents can move out of this queue while it is open; don't strand the
  // user on a page that no longer exists.
  const totalPages = list.data?.totalPages
  useEffect(() => {
    if (totalPages != null && page > 0 && page >= totalPages) {
      setView({ page: totalPages > 1 ? String(totalPages - 1) : undefined })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, totalPages])
  // Stalled documents sit in the processing queue but nothing is working on them.
  const processing =
    (stats.data?.byQueue.PROCESSING ?? 0) - (stats.data?.stalled ?? 0)

  // Live updates while anything is being read.
  const anyBusy = rows.some((r) => isBusy(r)) || processing > 0
  const { refetch: refetchList } = list
  const { refetch: refetchStats } = stats
  useEffect(() => {
    if (!anyBusy || !clientId) return undefined
    const id = setInterval(() => {
      refetchList()
      refetchStats()
    }, LIVE_POLL_MS)
    return () => clearInterval(id)
  }, [anyBusy, clientId, refetchList, refetchStats])

  const refresh = () => {
    refetchList()
    refetchStats()
  }

  const [getFile] = useGetDocumentFileMutation()
  const onDownload = async (doc: DocumentSummary) => {
    try {
      const blob = await getFile({ id: doc.id }).unwrap()
      downloadBlob(blob, doc.fileName || `${displayFileName(doc)}.pdf`)
    } catch {
      notifyError('Could not download the document. Please try again.')
    }
  }

  const openDocument = (doc: DocumentSummary) =>
    navigate(documentPath(doc), {
      state: {
        ids: rows.map((r) => r.id),
        from: `${ROUTES.DOCUMENTS}?${params}`
      }
    })

  if (!clientId) {
    return (
      <div className="dx">
        <PageHeader title="Documents" />
        <div className="dx-card">
          <EmptyState
            icon={<PageIcon />}
            title="Choose a client"
            description="Select a client in the header to see their documents."
          />
        </div>
      </div>
    )
  }

  const emptyCopy = QUEUE_EMPTY[queue ?? 'ALL']
  const listError = list.isError && !list.isFetching

  return (
    <div className="dx">
      <PageHeader
        breadcrumbs={[
          { label: 'Documents', to: ROUTES.DOCUMENTS },
          { label: clientName ?? 'Client' }
        ]}
        title={clientName ? `${clientName} — Documents` : 'Documents'}
        subtitle="Remittances and invoices received for this client, read by Document AI."
      />

      <div className="dx-inbox-bar">
        <span className="dx-inbox-bar__label">
          <EmailIcon /> Import via email
        </span>
        {inbox ? (
          <>
            <CopyChip value={inbox.emailId} label="inbox address" />
            {!inbox.enable && (
              <span
                className="dx-chip dx-chip--warning"
                title="This mailbox is not being checked"
              >
                Mailbox paused
              </span>
            )}
          </>
        ) : (
          <span className="dx-muted">No inbox set up for this client yet</span>
        )}
      </div>

      <nav className="dx-tabs" aria-label="Document queues">
        <div className="dx-tabs__group">
          {QUEUE_TABS.filter((t) => t.key).map((t) => {
            const count = t.key ? stats.data?.byQueue[t.key] : undefined
            return (
              <button
                key={t.key}
                type="button"
                className={classNames('dx-tab', {
                  'dx-tab--active': queue === t.key
                })}
                aria-current={queue === t.key ? 'page' : undefined}
                onClick={() => setView({ queue: t.key, page: undefined })}
              >
                {t.label}
                {count != null && (
                  <span
                    className={classNames(
                      'dx-pill',
                      `dx-pill--${count ? t.tone : 'neutral'}`
                    )}
                  >
                    {count.toLocaleString()}
                  </span>
                )}
              </button>
            )
          })}
        </div>
        <div className="dx-tabs__group">
          <button
            type="button"
            className={classNames('dx-tab', { 'dx-tab--active': !queue })}
            aria-current={!queue ? 'page' : undefined}
            onClick={() => setView({ queue: 'ALL', page: undefined })}
          >
            All documents
            {stats.data && (
              <span className="dx-pill dx-pill--neutral">
                {stats.data.total.toLocaleString()}
              </span>
            )}
          </button>
        </div>
      </nav>

      <div className="dx-toolbar">
        <div className="dx-toolbar__left">
          <Button
            appearance="primary"
            className="dx-btn-primary"
            startIcon={<FileUploadIcon />}
            onClick={() => setUploadOpen(true)}
          >
            Upload documents
          </Button>
          {processing > 0 && (
            <button
              type="button"
              className="dx-chip dx-chip--info dx-chip--button"
              onClick={() => setView({ queue: 'PROCESSING', page: undefined })}
            >
              <span className="dx-spinner" aria-hidden /> Processing{' '}
              {processing}
            </button>
          )}
        </div>
        <div className="dx-toolbar__right">
          <label className="dx-search" htmlFor="dx-document-search">
            <SearchIcon />
            <input
              id="dx-document-search"
              type="search"
              placeholder="Supplier, reference, file or sender"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              aria-label="Search documents"
            />
          </label>
          <button
            type="button"
            className="dx-icon-btn dx-icon-btn--bordered"
            onClick={refresh}
            disabled={list.isFetching}
            title="Refresh"
            aria-label="Refresh"
          >
            <ReloadIcon spin={list.isFetching} />
          </button>
        </div>
      </div>

      <div className="dx-card dx-card--flush">
        {listError ? (
          <EmptyState
            tone="danger"
            title="Couldn’t load documents"
            description="Check your connection and try again."
            action={
              <Button appearance="ghost" onClick={refresh}>
                Retry
              </Button>
            }
          />
        ) : (
          <DocumentsTable
            rows={rows}
            loading={list.isLoading}
            onOpen={openDocument}
            onDownload={onDownload}
            empty={
              <EmptyState
                icon={<PageIcon />}
                title={search ? 'No matching documents' : emptyCopy.title}
                description={
                  search
                    ? `Nothing in this view matches “${search}”.`
                    : emptyCopy.body
                }
                action={
                  search ? (
                    <Button
                      appearance="ghost"
                      onClick={() => setSearchText('')}
                    >
                      Clear search
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        )}
        {!listError && (list.data?.totalElements ?? 0) > 0 && (
          <Pagination
            page={page}
            size={size}
            total={list.data?.totalElements ?? 0}
            sizes={PAGE_SIZES}
            onPage={(p) => setView({ page: p ? String(p) : undefined })}
            onSize={(s) => setView({ size: String(s), page: undefined })}
          />
        )}
      </div>

      <UploadDocumentsModal
        open={uploadOpen}
        clientId={clientId}
        clientName={clientName}
        inboxEmail={inbox?.emailId}
        onClose={() => setUploadOpen(false)}
        onViewProcessing={() =>
          setView({ queue: 'PROCESSING', page: undefined, q: undefined })
        }
      />
    </div>
  )
}

export default DocumentList
